import React, { FC, ReactNode, useEffect, useState, type JSX } from 'react';
import { Props as ReactModalProps } from 'react-modal';
import { defineMessages, useIntl } from 'react-intl';
import * as S from './styles';
import { ErrorModal } from './ErrorModal';

const msgs = defineMessages({
  close: { id: 'close' },
});

const CloseButtonWrapper: FC<{
  children: JSX.Element;
  stickyCloseButton: boolean;
}> = ({ children, stickyCloseButton }) =>
  stickyCloseButton ? <S.CloseWrapper>{children}</S.CloseWrapper> : children;

export interface ModalProps extends ReactModalProps {
  handleClose?: (_event: React.MouseEvent) => void;
  backgroundColor?: 'white' | 'primary99';
  darkCloseIcon?: boolean;
  hideCloseButton?: boolean;
  shouldCloseOnOutsideClick?: boolean;
  shouldCloseOnEsc?: boolean;
  children?: ReactNode;
  stickyCloseButton?: boolean;
  fullSize?: boolean;
}

const Modal: FC<ModalProps> = ({
  isOpen: propsIsOpen,
  handleClose,
  children,
  darkCloseIcon = false,
  hideCloseButton = false,
  shouldCloseOnOutsideClick = true,
  shouldCloseOnEsc = true,
  backgroundColor = 'white',
  stickyCloseButton = false,
  fullSize = false,
  ...reactModalProps
}) => {
  const intl = useIntl();
  const closeText = intl.formatMessage(msgs.close);
  const [isOpen, setIsOpen] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      handleClose?.(e as unknown as React.MouseEvent);
    }
  };

  /* istanbul ignore next: tested manually */
  useEffect(() => {
    // In React strict mode, opens the modal asynchronously to prevent issues with conditional rendering
    // and with modals that are initially open. See this thread: https://github.com/reactjs/react-modal/issues/808
    setIsOpen(propsIsOpen);
  }, [propsIsOpen]);

  return (
    <S.Modal
      {...reactModalProps}
      isOpen={isOpen}
      shouldCloseOnEsc={shouldCloseOnEsc}
      shouldCloseOnOverlayClick={shouldCloseOnOutsideClick}
      onRequestClose={handleClose}
      $backgroundColor={backgroundColor}
      $fullSize={fullSize}
    >
      {!hideCloseButton && (
        <CloseButtonWrapper stickyCloseButton={stickyCloseButton}>
          <S.CloseButton
            onClick={handleClose}
            onKeyDown={handleKeyDown}
            title={closeText}
            aria-label={closeText}
          >
            <S.CloseIcon $darkCloseIcon={darkCloseIcon} name="close" />
          </S.CloseButton>
        </CloseButtonWrapper>
      )}
      {children}
    </S.Modal>
  );
};

export { Modal, ErrorModal };
